import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getIncomingChallenges,
  getOutgoingChallenges,
  getMyChessRating,
  getMyGames,
  acceptChallenge,
  declineChallenge,
  cancelChallenge,
  listOpenSeeks,
  createSeek,
  acceptSeek,
  cancelSeek,
  abortChessGame,
  getMyPlayBanStatus,
  appealChessBan,
  getPlayingPrefs,
  updatePlayingPrefs,
  getLiveGames,
  getChessCoachStatus,
} from '../../api/playing';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import UserLink from '../../components/common/UserLink';
import TimeControlFields from './TimeControlFields';
import TimeModeSelect from './TimeModeSelect';
import { PlayingBanner, PlayingSkeleton, formatTimeControl, formatDeadlineCountdown, formatExpiryCountdown, deadlineUrgency, colorPreferenceLabel, SEEK_TTL_MS, challengeTtlMs, isPastTtl } from './playingUi';
import { PlayingDisclosure } from './PlayingDisclosure';
import { formatDateTime } from '../../utils/regionalFormat';
import { subscribeIncomingChallenges, subscribeChessSeeks, notifyPlayingChallengesChanged } from '../../playing/chessSocket';
import { setPlayingMuted } from '../../playing/chessSounds';
import { warmOpeningBook } from '../../playing/openingBook';
import { usePlayingConfirm } from './PlayingConfirmDialog';
import {
  CompositionDonut,
  CHART_COLORS,
} from '../../components/charts';
import {
  MarkKnight,
  MarkSwords,
  MarkChallenge,
  MarkSettings,
  MarkPawn,
  PieceTileGlyph,
  ShortcutMark,
} from '../../components/playing/PlayingHubMarks';
import { getRoles, getActiveRole } from '../../utils/auth';
import { apiErrorMessage } from '../../utils/apiError';

const HUB_SPEEDS = ['BULLET', 'BLITZ', 'RAPID', 'CLASSICAL', 'CORRESPONDENCE'];

function parseHubSpeed(raw) {
  const v = String(raw || '').toUpperCase();
  return HUB_SPEEDS.includes(v) ? v : 'BLITZ';
}

const QUICK_LINKS = [
  { to: '/playing/challenge', key: 'challengePlayer', desc: 'challengeShortcut' },
  { to: '/playing/games', key: 'myGames', desc: 'myGamesShortcut' },
  { to: '/playing/ai-coach', key: 'aiCoach', desc: 'aiCoachShortcut' },
  { to: '/playing/coach-board', key: 'coachBoard', desc: 'coachBoardShortcut' },
  { to: '/playing/live', key: 'liveGames', desc: 'viewLive' },
  { to: '/playing/leaderboard', key: 'leaderboard', desc: 'viewLeaderboard' },
];

export default function PlayingHubPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requestConfirm, dialog: confirmDialog } = usePlayingConfirm();
  const [rating, setRating] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [seeks, setSeeks] = useState([]);
  const [mySeek, setMySeek] = useState(null);
  const [activeGames, setActiveGames] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [coachLive, setCoachLive] = useState(false);
  const [level, setLevel] = useState(3);
  const [rated, setRated] = useState(false);
  const [seekRated, setSeekRated] = useState(false);
  const [seekColor, setSeekColor] = useState('RANDOM');
  const [timeControlSeconds, setTimeControlSeconds] = useState(600);
  const [incrementSeconds, setIncrementSeconds] = useState(0);
  const [seekTimeControlSeconds, setSeekTimeControlSeconds] = useState(600);
  const [seekIncrementSeconds, setSeekIncrementSeconds] = useState(0);
  const [seekDaysPerMove, setSeekDaysPerMove] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [playBan, setPlayBan] = useState(null);
  const [banStatusUnknown, setBanStatusUnknown] = useState(false);
  const [partialLoadHint, setPartialLoadHint] = useState('');
  const [appealMessage, setAppealMessage] = useState('');
  const [appealBusy, setAppealBusy] = useState(false);
  const [appealDone, setAppealDone] = useState(false);
  const [hubSpeed, setHubSpeed] = useState(() => parseHubSpeed(searchParams.get('speed')));
  const [prefs, setPrefs] = useState(null);
  const [prefsLoadFailed, setPrefsLoadFailed] = useState(false);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [prefsNotice, setPrefsNotice] = useState('');
  const [livePeek, setLivePeek] = useState([]);
  const [livePeekError, setLivePeekError] = useState('');
  const [reviewReadyPeek, setReviewReadyPeek] = useState([]);
  const [ttlNow, setTtlNow] = useState(() => Date.now());
  const [playTab, setPlayTab] = useState('computer');
  /** Board settings stay collapsed until requested (Lichess-style progressive disclosure). */
  const [boardSettingsOpen, setBoardSettingsOpen] = useState(false);
  /** Real ban blocks everything; unknown status only pauses human matchmaking. */
  const banned = !!playBan;
  const matchmakingBlocked = banned || banStatusUnknown;
  const isParentRole = String(getActiveRole() || '').toUpperCase() === 'PARENT'
    || (getRoles() || []).some((r) => String(r).toUpperCase().replace(/^ROLE_/, '') === 'PARENT');
  const activePlayTab = isParentRole ? 'computer' : playTab;
  const liveIncoming = challenges.filter((c) => !isPastTtl(c.createdAt, challengeTtlMs(c), ttlNow));
  const liveOutgoing = outgoing.filter((c) => !isPastTtl(c.createdAt, challengeTtlMs(c), ttlNow));
  /** Hub stays quiet when empty — lists live on /playing/challenge; appear here only when actionable. */
  const showHubChallenges = liveIncoming.length > 0 || liveOutgoing.length > 0;

  const savePref = async (patch) => {
    setPrefsBusy(true);
    try {
      const updated = await updatePlayingPrefs(patch);
      if (Object.prototype.hasOwnProperty.call(patch, 'soundEnabled')) {
        setPlayingMuted(!patch.soundEnabled);
      }
      setPrefs(updated);
      setPrefsNotice(t('playing.prefsSaved', { defaultValue: 'Preferences saved' }));
      setTimeout(() => setPrefsNotice(''), 2000);
    } catch {
      setError(t('playing.prefsSaveFailed', {
        defaultValue: 'Could not sync preferences to the server.',
      }));
    } finally {
      setPrefsBusy(false);
    }
  };

  const changeHubSpeed = (sc) => {
    setHubSpeed(sc);
    const next = new URLSearchParams(searchParams);
    if (sc === 'BLITZ') next.delete('speed');
    else next.set('speed', sc);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const fromUrl = parseHubSpeed(searchParams.get('speed'));
    setHubSpeed((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  useEffect(() => {
    const id = window.setInterval(() => setTtlNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getChessCoachStatus()
      .then((s) => {
        if (!cancelled) setCoachLive(!!(s?.enabled && s?.configured));
      })
      .catch(() => {
        if (!cancelled) setCoachLive(false);
      });
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    try {
      let partial = false;
      let banUnknown = false;
      const [r, c, out, games, openSeeks, ban, playingPrefs] = await Promise.all([
        getMyChessRating(hubSpeed),
        getIncomingChallenges(),
        getOutgoingChallenges().catch(() => { partial = true; return []; }),
        getMyGames().catch(() => { partial = true; return []; }),
        listOpenSeeks().catch(() => { partial = true; return []; }),
        getMyPlayBanStatus().catch(() => { banUnknown = true; partial = true; return null; }),
        getPlayingPrefs().catch(() => { partial = true; return null; }),
      ]);
      setRating(r);
      setChallenges(Array.isArray(c) ? c : []);
      setOutgoing(Array.isArray(out) ? out : []);
      const seekList = Array.isArray(openSeeks) ? openSeeks : [];
      setSeeks(seekList.filter((s) => !s.mine));
      setMySeek(seekList.find((s) => s.mine) || null);
      if (playingPrefs) {
        setPrefs(playingPrefs);
        setPrefsLoadFailed(false);
        if (typeof playingPrefs.soundEnabled === 'boolean') {
          setPlayingMuted(!playingPrefs.soundEnabled);
        }
        if (playingPrefs.lastTimeControlSeconds != null && Number(playingPrefs.lastTimeControlSeconds) > 0) {
          setTimeControlSeconds(Number(playingPrefs.lastTimeControlSeconds));
          setSeekTimeControlSeconds(Number(playingPrefs.lastTimeControlSeconds));
        }
        if (playingPrefs.lastIncrementSeconds != null && Number(playingPrefs.lastIncrementSeconds) >= 0) {
          setIncrementSeconds(Number(playingPrefs.lastIncrementSeconds));
          setSeekIncrementSeconds(Number(playingPrefs.lastIncrementSeconds));
        }
      } else {
        setPrefsLoadFailed(true);
      }
      const finished = new Set(['WHITE_WON', 'BLACK_WON', 'DRAW', 'ABORTED']);
      const gameRows = Array.isArray(games) ? games : (Array.isArray(games?.games) ? games.games : []);
      const active = gameRows.filter((g) => {
        const st = String(g?.status || '').toUpperCase();
        if (finished.has(st) || g?.endedAt) return false;
        return st === 'ACTIVE' || st === 'PENDING';
      });
      active.sort((a, b) => {
        const aTurn = a.myTurn ? 0 : 1;
        const bTurn = b.myTurn ? 0 : 1;
        if (aTurn !== bTurn) return aTurn - bTurn;
        const aPend = a.status === 'PENDING' ? 0 : 1;
        const bPend = b.status === 'PENDING' ? 0 : 1;
        return aPend - bPend;
      });
      setActiveGames(active);
      const finishedStatuses = new Set(['WHITE_WON', 'BLACK_WON', 'DRAW']);
      const reviewReady = gameRows
        .filter((g) => {
          const st = String(g?.status || '').toUpperCase();
          if (!finishedStatuses.has(st) && !g?.endedAt) return false;
          if (String(g?.gameType || '').toUpperCase() === 'VS_COMPUTER') return false;
          return !!g.clubReviewReady;
        })
        .slice(0, 3);
      setReviewReadyPeek(reviewReady);
      setBanStatusUnknown(banUnknown);
      setPlayBan(!banUnknown && ban && ban.active ? ban : null);
      if (ban?.appealPending) setAppealDone(true);
      setError('');
      setPartialLoadHint(partial ? t('playing.hubPartialLoad') : '');
      getLiveGames()
        .then((rows) => {
          const list = (Array.isArray(rows) ? rows : [])
            .filter((g) => g?.id
              && g.gameType !== 'VS_COMPUTER'
              && g.spectatorAllowed !== false
              && (g.status === 'ACTIVE' || g.status === 'PENDING'))
            .slice(0, 3);
          setLivePeek(list);
          setLivePeekError('');
        })
        .catch(() => {
          setLivePeek([]);
          setLivePeekError(t('playing.livePeekLoadFailed', {
            defaultValue: 'Could not load live games right now.',
          }));
        });
    } catch {
      setError(t('playing.loadFailed'));
      setPartialLoadHint('');
      setBanStatusUnknown(true);
      setLivePeek([]);
      setLivePeekError('');
      setReviewReadyPeek([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    warmOpeningBook();
    try {
      if (sessionStorage.getItem('playing.abortLeaveFailed') === '1') {
        sessionStorage.removeItem('playing.abortLeaveFailed');
        setError(t('playing.abortLeaveFailed', {
          defaultValue: 'Could not cancel your waiting challenge. It may still appear for your opponent until it expires.',
        }));
      }
    } catch {
      /* ignore */
    }
    const iv = setInterval(load, 8000);
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [hubSpeed]);

  useEffect(() => {
    const uid = rating?.userId;
    if (uid == null) return undefined;
    return subscribeIncomingChallenges(uid, (c) => {
      if (!c?.id) return;
      const status = (c.status || 'PENDING').toUpperCase();
      if (status === 'PENDING') {
        if (Number(c.toUserId) === Number(uid)) {
          setChallenges((prev) => (prev.some((x) => Number(x.id) === Number(c.id)) ? prev : [c, ...prev]));
        }
        if (Number(c.fromUserId) === Number(uid)) {
          setOutgoing((prev) => (prev.some((x) => Number(x.id) === Number(c.id)) ? prev : [c, ...prev]));
        }
        return;
      }
      setChallenges((prev) => prev.filter((x) => Number(x.id) !== Number(c.id)));
      setOutgoing((prev) => prev.filter((x) => Number(x.id) !== Number(c.id)));
    });
  }, [rating?.userId]);

  useEffect(() => {
    const uid = rating?.userId;
    if (uid == null) return undefined;
    return subscribeChessSeeks((s) => {
      if (!s?.id) return;
      const status = (s.status || 'OPEN').toUpperCase();
      const forMe = Number(s.userId) === Number(uid);
      if (status === 'MATCHED' && s.gameId) {
        setSeeks((prev) => prev.filter((x) => Number(x.id) !== Number(s.id)));
        setMySeek((cur) => (cur != null && Number(cur.id) === Number(s.id) ? null : cur));
        // Owner: open board immediately (Lichess-style); toast remains backup Open CTA
        if (forMe && window.location.pathname !== `/playing/game/${s.gameId}`) {
          navigate(`/playing/game/${s.gameId}`);
        }
        return;
      }
      if (status === 'OPEN') {
        if (forMe) {
          setMySeek(s);
          setSeeks((prev) => prev.filter((x) => Number(x.id) !== Number(s.id)));
        } else {
          setSeeks((prev) => (prev.some((x) => Number(x.id) === Number(s.id))
            ? prev.map((x) => (Number(x.id) === Number(s.id) ? s : x))
            : [s, ...prev]));
        }
        return;
      }
      setSeeks((prev) => prev.filter((x) => Number(x.id) !== Number(s.id)));
      setMySeek((cur) => (cur != null && Number(cur.id) === Number(s.id) ? null : cur));
    }, undefined, uid);
  }, [rating?.userId, navigate]);

  const startAi = async (color) => {
    if (banned) return;
    if (rated && !(await requestConfirm({ message: t('playing.ratedConfirm') }))) return;
    navigate(
      `/playing/play?color=${color}&level=${level}&rated=${rated ? '1' : '0'}`
      + `&tc=${timeControlSeconds}&inc=${incrementSeconds}`
    );
  };

  const startSeek = async () => {
    if (matchmakingBlocked) return;
    if (seekRated && !(await requestConfirm({ message: t('playing.ratedConfirm') }))) return;
    setBusyId('seek');
    setError('');
    try {
      const s = await createSeek({
        colorPreference: seekColor,
        rated: seekRated,
        timeControlSeconds: seekDaysPerMove > 0 ? 0 : seekTimeControlSeconds,
        incrementSeconds: seekDaysPerMove > 0 ? 0 : seekIncrementSeconds,
        daysPerMove: seekDaysPerMove > 0 ? seekDaysPerMove : null,
      });
      if (s?.status === 'MATCHED' && s.gameId) {
        navigate(`/playing/game/${s.gameId}`);
        return;
      }
      setMySeek(s);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.seekFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-shell-form playing-page playing-page--arena">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={t('playing.hubTitle')}
        subtitle={t('playing.hubSubtitle')}
        aside={(
          isParentRole ? (
            <Button variant="page-primary" onClick={() => navigate('/playing/play')}>
              {t('playing.playComputer', { defaultValue: 'Play computer' })}
            </Button>
          ) : (
            <Button
              variant="page-primary"
              disabled={matchmakingBlocked}
              title={matchmakingBlocked
                ? (banned
                  ? t('playing.banQuickLinkBlocked')
                  : t('playing.banStatusUnknown', {
                    defaultValue: 'Could not verify play-ban status. Matchmaking is paused until this refreshes.',
                  }))
                : undefined}
              onClick={() => navigate('/playing/challenge')}
            >
              {t('playing.challengePlayer')}
            </Button>
          )
        )}
      />

      <div className="playing-hub-urgency">
        {playBan && (
          <PlayingBanner tone="danger" live="assertive">
            <p className="font-semibold">{t('playing.banBanner')}</p>
            {playBan.reason && (
              <p className="mt-1 text-sm">{t('playing.banBannerReason', { reason: playBan.reason })}</p>
            )}
            <p className="mt-1 text-sm">
              {playBan.expiresAt
                ? t('playing.banBannerUntil', { when: formatDateTime(playBan.expiresAt) })
                : t('playing.banBannerIndefinite')}
            </p>
            {appealDone || playBan?.appealPending ? (
              <p className="mt-2 text-sm font-medium text-emerald-100">
                {t('playing.appealPending', {
                  defaultValue: 'Appeal pending — staff will review it.',
                })}
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {playBan?.lastAppealRejectNote && (
                  <p className="text-sm text-rose-100">
                    {t('playing.appealRejectedNote', {
                      note: playBan.lastAppealRejectNote,
                      defaultValue: 'Previous appeal rejected: {{note}}',
                    })}
                  </p>
                )}
                <textarea
                  className="input w-full text-sm text-slate-900"
                  rows={2}
                  value={appealMessage}
                  onChange={(e) => setAppealMessage(e.target.value)}
                  placeholder={t('playing.appealPlaceholder', {
                    defaultValue: 'Explain why this ban should be lifted…',
                  })}
                />
                <Button
                  variant="page-secondary"
                  disabled={appealBusy || !appealMessage.trim()}
                  onClick={async () => {
                    setAppealBusy(true);
                    try {
                      await appealChessBan(appealMessage.trim());
                      setAppealDone(true);
                      setAppealMessage('');
                      setPlayBan((b) => (b ? { ...b, appealPending: true } : b));
                    } catch (e) {
                      const msg = e?.response?.data?.message || '';
                      if (/open appeal already exists/i.test(msg)) {
                        setAppealDone(true);
                        setPlayBan((b) => (b ? { ...b, appealPending: true } : b));
                      } else {
                        setError(apiErrorMessage(e, t('playing.appealFailed', {
                          defaultValue: 'Could not submit appeal.',
                        })));
                      }
                    } finally {
                      setAppealBusy(false);
                    }
                  }}
                >
                  {t('playing.submitAppeal', { defaultValue: 'Appeal ban' })}
                </Button>
              </div>
            )}
          </PlayingBanner>
        )}

        {banStatusUnknown && !playBan && (
          <PlayingBanner tone="warning">
            <p>
              {t('playing.banStatusUnknown', {
                defaultValue: 'Could not verify play-ban status. Matchmaking is paused until this refreshes.',
              })}
            </p>
            <div className="mt-2">
              <Button variant="page-secondary" onClick={() => load()}>
                {t('playing.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          </PlayingBanner>
        )}

        {error && (
          <PlayingBanner tone="danger">
            <p>{error}</p>
            <div className="mt-2">
              <Button variant="page-secondary" onClick={() => { setError(''); load(); }}>
                {t('playing.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          </PlayingBanner>
        )}

        {partialLoadHint && !error && (
          <PlayingBanner tone="warning">
            <p>{partialLoadHint}</p>
            <div className="mt-2">
              <Button variant="page-secondary" onClick={() => load()}>
                {t('playing.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          </PlayingBanner>
        )}

        {activeGames.length > 0 && (
          <div className="playing-continue-banner !block">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                {t('playing.continueGame')}
                {activeGames.length > 1 ? ` · ${activeGames.length}` : ''}
              </p>
              {activeGames.length > 3 && (
                <Button variant="page-secondary" onClick={() => navigate('/playing/games')}>
                  {t('playing.activeGames')}
                </Button>
              )}
            </div>
            <ul className="space-y-3">
              {activeGames.slice(0, 3).map((g) => {
                const tc = formatTimeControl(
                  g.timeControlSeconds,
                  g.incrementMs != null ? Math.round(g.incrementMs / 1000) : 0,
                  g.daysPerMove,
                );
                const deadline = formatDeadlineCountdown(g.moveDeadlineAt, ttlNow);
                const urgency = deadlineUrgency(g.moveDeadlineAt, ttlNow);
                return (
                  <li key={g.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-100/80 bg-white/70 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">
                        <UserLink id={g.whiteUserId} name={g.whiteName} playing className="text-slate-900 hover:text-primary-700" />
                        {' vs '}
                        <UserLink id={g.blackUserId} name={g.blackName} playing className="text-slate-900 hover:text-primary-700" />
                        <span className="ml-2 text-xs font-normal tabular-nums text-slate-400">#{g.id}</span>
                      </p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {tc && <span className="playing-meta-pill">{tc}</span>}
                        {g.myTurn && (
                          <span className="playing-meta-pill playing-meta-pill--accent">{t('playing.yourMove')}</span>
                        )}
                        {deadline && (
                          <span
                            className={`playing-meta-pill ${
                              urgency === 'critical'
                                ? 'playing-meta-pill--danger'
                                : urgency === 'warn'
                                  ? 'playing-meta-pill--warn'
                                  : ''
                            }`}
                          >
                            {t('playing.moveDeadlineShort', {
                              when: deadline,
                              defaultValue: 'Move by {{when}}',
                            })}
                          </span>
                        )}
                        <span className="playing-meta-pill">
                          {g.rated ? t('playing.ratedGame') : t('playing.casualGame')}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="page-primary" onClick={() => navigate(`/playing/game/${g.id}`)}>
                        {g.status === 'PENDING' ? t('playing.open') : t('playing.resume')}
                      </Button>
                      {g.status === 'PENDING'
                        && rating?.userId != null
                        && Number(g.challengeFromUserId) === Number(rating.userId) && (
                        <Button
                          variant="page-secondary"
                          disabled={busyId === `abort-pending-${g.id}`}
                          onClick={async () => {
                            const gid = g.id;
                            setBusyId(`abort-pending-${gid}`);
                            try {
                              await abortChessGame(gid);
                              setActiveGames((prev) => prev.filter((x) => Number(x.id) !== Number(gid)));
                            } catch {
                              setError(t('playing.abortFailed', { defaultValue: 'Could not cancel this game.' }));
                            } finally {
                              setBusyId(null);
                            }
                          }}
                        >
                          {t('playing.cancelChallenge')}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {(() => {
              const featuredIds = new Set(activeGames.slice(0, 3).map((g) => Number(g.id)));
              const yourMoveRest = activeGames.filter((g) => g.myTurn && !featuredIds.has(Number(g.id)));
              const yourMoveTotal = activeGames.filter((g) => g.myTurn).length;
              if (yourMoveRest.length === 0) return null;
              return (
                <div className="mt-3 border-t border-emerald-100 pt-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                      {t('playing.yourMoveStrip', { defaultValue: 'Your move' })}
                      <span className="ml-1.5 tabular-nums">({yourMoveTotal})</span>
                    </p>
                    {yourMoveRest.length > 3 && (
                      <Button variant="page-secondary" onClick={() => navigate('/playing/games?filter=YOUR_MOVE')}>
                        {t('playing.viewAllYourMoves', { defaultValue: 'View all your moves' })}
                      </Button>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {yourMoveRest.slice(0, 3).map((g) => {
                      const tc = formatTimeControl(
                        g.timeControlSeconds,
                        g.incrementMs != null ? Math.round(g.incrementMs / 1000) : 0,
                        g.daysPerMove,
                      );
                      const deadline = formatDeadlineCountdown(g.moveDeadlineAt, ttlNow);
                      const urgency = deadlineUrgency(g.moveDeadlineAt, ttlNow);
                      return (
                        <li key={g.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900">
                              <UserLink id={g.whiteUserId} name={g.whiteName} playing className="text-slate-900 hover:text-primary-700" />
                              {' vs '}
                              <UserLink id={g.blackUserId} name={g.blackName} playing className="text-slate-900 hover:text-primary-700" />
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              {tc && <span className="playing-meta-pill">{tc}</span>}
                              {deadline && (
                                <span
                                  className={`playing-meta-pill ${
                                    urgency === 'critical'
                                      ? 'playing-meta-pill--danger'
                                      : urgency === 'warn'
                                        ? 'playing-meta-pill--warn'
                                        : ''
                                  }`}
                                >
                                  {t('playing.moveDeadlineShort', {
                                    when: deadline,
                                    defaultValue: 'Move by {{when}}',
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button variant="page-primary" onClick={() => navigate(`/playing/game/${g.id}`)}>
                            {t('playing.yourMove')}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <div className="playing-hub-arena">
      <div className="playing-hub-grid">
        <div className="playing-hub-primary">
          <section className="playing-hub-stage">
          {!isParentRole && (
            <div className="playing-hub-tabs" role="tablist" aria-label={t('playing.hubTitle')}>
              <button
                type="button"
                role="tab"
                aria-selected={activePlayTab === 'computer'}
                className={`playing-hub-tab${activePlayTab === 'computer' ? ' playing-hub-tab--active' : ''}`}
                onClick={() => setPlayTab('computer')}
              >
                <MarkKnight />
                {t('playing.playComputer')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activePlayTab === 'match'}
                className={`playing-hub-tab${activePlayTab === 'match' ? ' playing-hub-tab--active' : ''}`}
                onClick={() => setPlayTab('match')}
              >
                <MarkSwords />
                {t('playing.quickMatch')}
              </button>
            </div>
          )}

          {(activePlayTab === 'computer' || isParentRole) && (
              <div className="playing-hub-stage__inner">
                {isParentRole && (
                  <header className="playing-hub-stage__header">
                    <h2 className="playing-hub-stage__title">{t('playing.playComputer')}</h2>
                  </header>
                )}
                <div className="playing-hub-field">
                  <div className="playing-hub-field-row">
                    <span className="playing-hub-field-label">{t('playing.computerLevel')}</span>
                    <strong className="playing-hub-field-value tabular-nums">
                      {level}
                      <span className="playing-hub-field-value__band">
                        {t(`playing.levelBand.${level <= 2 ? 'beginner' : level <= 4 ? 'intermediate' : level <= 7 ? 'club' : 'strong'}`)}
                      </span>
                    </strong>
                  </div>
                  <div className="playing-hub-pawn-meter" aria-hidden>
                    {Array.from({ length: 10 }, (_, i) => (
                      <span
                        key={i}
                        className={`playing-hub-pawn-meter__pawn${i < level ? ' playing-hub-pawn-meter__pawn--on' : ''}`}
                      >
                        <MarkPawn filled={i < level} />
                      </span>
                    ))}
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={level}
                    onChange={(e) => setLevel(Number(e.target.value))}
                    className="playing-hub-range"
                    aria-label={t('playing.computerLevel')}
                  />
                  <div className="playing-hub-range-ends">
                    <span>{t('playing.levelEasy')}</span>
                    <span>{t('playing.levelHard')}</span>
                  </div>
                </div>
                <TimeControlFields
                  layout="hub"
                  timeControlSeconds={timeControlSeconds}
                  incrementSeconds={incrementSeconds}
                  onChange={({ timeControlSeconds: tc, incrementSeconds: inc }) => {
                    setTimeControlSeconds(tc);
                    setIncrementSeconds(inc);
                    updatePlayingPrefs({ lastTimeControlSeconds: tc, lastIncrementSeconds: inc })
                      .then((p) => { if (p) setPrefs(p); })
                      .catch(() => {});
                  }}
                />
                <label className="playing-hub-check">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 bg-white text-slate-800 focus:ring-slate-400"
                    checked={rated}
                    onChange={(e) => setRated(e.target.checked)}
                  />
                  {t('playing.ratedGame')}
                </label>
                <p className="playing-hub-stage__hint playing-hub-stage__hint--solo">
                  {t('playing.chooseColorToStart', {
                    defaultValue: 'Choose a side to start against the computer.',
                  })}
                </p>
                <div className="playing-hub-color-row" role="group" aria-label={t('playing.yourColor')}>
                  <button
                    type="button"
                    className="playing-hub-color-tile playing-hub-color-tile--white"
                    disabled={banned}
                    aria-label={t('playing.playAsWhiteVsComputer', {
                      defaultValue: 'Play as White against the computer',
                    })}
                    title={banned ? t('playing.banQuickLinkBlocked') : t('playing.playAsWhiteVsComputer', {
                      defaultValue: 'Play as White against the computer',
                    })}
                    onClick={() => startAi('white')}
                  >
                    <PieceTileGlyph color="white" />
                    <span className="playing-hub-color-tile__label">
                      {t('playing.colorWhite', { defaultValue: 'White' })}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="playing-hub-color-tile playing-hub-color-tile--random"
                    disabled={banned}
                    aria-label={t('playing.playAsRandomVsComputer', {
                      defaultValue: 'Play a random side against the computer',
                    })}
                    title={banned ? t('playing.banQuickLinkBlocked') : t('playing.playAsRandomVsComputer', {
                      defaultValue: 'Play a random side against the computer',
                    })}
                    onClick={() => startAi('random')}
                  >
                    <PieceTileGlyph color="random" />
                    <span className="playing-hub-color-tile__label">
                      {t('playing.colorRandomSide', { defaultValue: 'Random side' })}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="playing-hub-color-tile playing-hub-color-tile--black"
                    disabled={banned}
                    aria-label={t('playing.playAsBlackVsComputer', {
                      defaultValue: 'Play as Black against the computer',
                    })}
                    title={banned ? t('playing.banQuickLinkBlocked') : t('playing.playAsBlackVsComputer', {
                      defaultValue: 'Play as Black against the computer',
                    })}
                    onClick={() => startAi('black')}
                  >
                    <PieceTileGlyph color="black" />
                    <span className="playing-hub-color-tile__label">
                      {t('playing.colorBlack', { defaultValue: 'Black' })}
                    </span>
                  </button>
                </div>
              </div>
          )}

          {!isParentRole && activePlayTab === 'match' && (
              <div className="playing-hub-stage__inner">
                <p className="playing-hub-stage__hint playing-hub-stage__hint--solo">
                  {t('playing.quickMatchDesc')}
                </p>
                <TimeModeSelect value={seekDaysPerMove} onChange={setSeekDaysPerMove} />
                {seekDaysPerMove <= 0 ? (
                  <TimeControlFields
                    layout="hub"
                    timeControlSeconds={seekTimeControlSeconds}
                    incrementSeconds={seekIncrementSeconds}
                    onChange={({ timeControlSeconds: tc, incrementSeconds: inc }) => {
                      setSeekTimeControlSeconds(tc);
                      setSeekIncrementSeconds(inc);
                      updatePlayingPrefs({ lastTimeControlSeconds: tc, lastIncrementSeconds: inc })
                        .then((p) => { if (p) setPrefs(p); })
                        .catch(() => {});
                    }}
                  />
                ) : (
                  <p className="text-sm text-slate-500">
                    {t('playing.correspondenceHint', {
                      n: seekDaysPerMove,
                      defaultValue: 'Each player has {{n}} days to make a move. No ticking clock.',
                    })}
                  </p>
                )}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 bg-white text-slate-800 focus:ring-slate-400"
                      checked={seekRated}
                      onChange={(e) => setSeekRated(e.target.checked)}
                    />
                    {t('playing.ratedGame')}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <span>{t('playing.yourColor')}</span>
                    <select
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-800"
                      value={seekColor}
                      onChange={(e) => setSeekColor(e.target.value)}
                    >
                      <option value="RANDOM">{t('playing.colorRandom')}</option>
                      <option value="WHITE">{t('playing.colorWhite')}</option>
                      <option value="BLACK">{t('playing.colorBlack')}</option>
                    </select>
                  </label>
                </div>
                {mySeek ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('playing.seekWaiting')}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {formatTimeControl(mySeek.timeControlSeconds, mySeek.incrementSeconds || 0, mySeek.daysPerMove)}
                        {' · '}
                        {mySeek.rated ? t('playing.ratedGame') : t('playing.casualGame')}
                        {formatExpiryCountdown(mySeek.createdAt, SEEK_TTL_MS, ttlNow) && (
                          <>
                            {' · '}
                            {t('playing.expiresIn', {
                              when: formatExpiryCountdown(mySeek.createdAt, SEEK_TTL_MS, ttlNow),
                              defaultValue: 'Expires in {{when}}',
                            })}
                          </>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="page-secondary"
                      disabled={busyId === `cancel-seek-${mySeek.id}`}
                      onClick={async () => {
                        setBusyId(`cancel-seek-${mySeek.id}`);
                        try {
                          await cancelSeek(mySeek.id);
                          setMySeek(null);
                        } catch (e) {
                          setError(apiErrorMessage(e, t('playing.cancelSeekFailed', {
                            defaultValue: 'Could not cancel this seek.',
                          })));
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      {t('playing.cancelSeek')}
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="playing-hub-find-btn"
                    disabled={matchmakingBlocked || busyId === 'seek'}
                    title={matchmakingBlocked
                      ? (banned
                        ? t('playing.banQuickLinkBlocked')
                        : t('playing.banStatusUnknown', {
                          defaultValue: 'Could not verify play-ban status. Matchmaking is paused until this refreshes.',
                        }))
                      : undefined}
                    onClick={startSeek}
                  >
                    <MarkSwords />
                    {t('playing.findOpponent')}
                  </button>
                )}
                {seeks.filter((s) => !isPastTtl(s.createdAt, SEEK_TTL_MS, ttlNow)).length > 0 ? (
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                    {seeks
                      .filter((s) => !isPastTtl(s.createdAt, SEEK_TTL_MS, ttlNow))
                      .map((s) => {
                        const tc = formatTimeControl(s.timeControlSeconds, s.incrementSeconds || 0, s.daysPerMove);
                        const expires = formatExpiryCountdown(s.createdAt, SEEK_TTL_MS, ttlNow);
                        return (
                          <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">
                                <UserLink id={s.userId} name={s.userName} playing className="text-slate-900 hover:text-slate-700" />
                                {s.rating != null ? ` · ${s.rating}` : ''}
                                {s.speedClass ? (
                                  <span className="ml-1 text-xs font-medium text-slate-500">
                                    · {t(`playing.speed.${s.speedClass}`, { defaultValue: s.speedClass })}
                                  </span>
                                ) : null}
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {tc && <span className="playing-tc-chip playing-tc-chip--sm">{tc}</span>}
                                <span className="playing-meta-pill">
                                  {s.rated ? t('playing.ratedGame') : t('playing.casualGame')}
                                </span>
                                <span className="playing-meta-pill">
                                  {colorPreferenceLabel(s.colorPreference, t)}
                                </span>
                                {s.spectatorAllowed === false && (
                                  <span className="playing-meta-pill">{t('playing.spectatorsOff')}</span>
                                )}
                                {expires && (
                                  <span className="playing-meta-pill playing-meta-pill--warn">
                                    {t('playing.expiresIn', {
                                      when: expires,
                                      defaultValue: 'Expires in {{when}}',
                                    })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="page-primary"
                              disabled={busyId === `seek-${s.id}`}
                              onClick={async () => {
                                setBusyId(`seek-${s.id}`);
                                try {
                                  const g = await acceptSeek(s.id);
                                  navigate(`/playing/game/${g.id}`);
                                } catch (e) {
                                  setError(apiErrorMessage(e, t('playing.acceptSeekFailed', {
                                    defaultValue: 'Could not join this seek.',
                                  })));
                                  await load();
                                } finally {
                                  setBusyId(null);
                                }
                              }}
                            >
                              {t('playing.playNow')}
                            </Button>
                          </li>
                        );
                      })}
                  </ul>
                ) : (
                  !mySeek && (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-500">
                        {seeks.length > 0
                          ? t('playing.noOpenSeeksExpired', {
                            defaultValue: 'Open seeks expired — create a new one or send a challenge.',
                          })
                          : t('playing.noOpenSeeks', {
                            defaultValue: 'No open seeks right now — create one above or try a challenge.',
                          })}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="page-secondary" onClick={() => navigate('/playing/challenge')}>
                          {t('playing.challengePlayer')}
                        </Button>
                        <Button variant="page-secondary" onClick={() => navigate('/playing/play')}>
                          {t('playing.playComputer', { defaultValue: 'Play computer' })}
                        </Button>
                      </div>
                    </div>
                  )
                )}
              </div>
          )}
          </section>

          {showHubChallenges && (
            <section className="playing-hub-panel" id="hub-challenges" aria-live="polite">
              <div className="playing-hub-panel__head">
                <div>
                  <h2 className="playing-hub-panel__title">
                    <span className="playing-hub-panel__title-icon"><MarkChallenge /></span>
                    {t('playing.challenges')}
                    <span className="playing-hub-count">
                      {liveIncoming.length + liveOutgoing.length}
                    </span>
                  </h2>
                  <p className="playing-hub-panel__hint">
                    {liveIncoming.length > 0
                      ? t('playing.challengesHint')
                      : t('playing.outgoingChallengesHint')}
                  </p>
                </div>
                {!isParentRole && (
                  <Link to="/playing/challenge" className="playing-text-link text-sm font-medium">
                    {t('playing.challengePlayer')}
                  </Link>
                )}
              </div>
              {liveIncoming.length > 0 && (
                <ul className="divide-y divide-slate-100">
                  {liveIncoming.map((c) => {
                    const tc = formatTimeControl(c.timeControlSeconds, c.incrementSeconds || 0, c.daysPerMove);
                    const expires = formatExpiryCountdown(c.createdAt, challengeTtlMs(c), ttlNow);
                    return (
                      <li key={`in-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-stone-400">
                            {t('playing.incomingChallenge', { defaultValue: 'Incoming' })}
                          </p>
                          <p className="text-sm font-semibold text-slate-900">
                            <UserLink id={c.fromUserId} name={c.fromName} playing className="text-slate-900 hover:text-primary-700" />
                          </p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {tc && <span className="playing-tc-chip playing-tc-chip--sm">{tc}</span>}
                            <span className="playing-meta-pill">
                              {c.rated ? t('playing.ratedGame') : t('playing.casualGame')}
                            </span>
                            <span className="playing-meta-pill">
                              {colorPreferenceLabel(c.colorPreference, t)}
                            </span>
                            {c.spectatorAllowed === false && (
                              <span className="playing-meta-pill">{t('playing.spectatorsOff')}</span>
                            )}
                            {expires && (
                              <span className="playing-meta-pill playing-meta-pill--warn">
                                {t('playing.expiresIn', {
                                  when: expires,
                                  defaultValue: 'Expires in {{when}}',
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!isParentRole && (
                            <Button
                              variant="page-primary"
                              disabled={busyId === c.id}
                              onClick={async () => {
                                setBusyId(c.id);
                                try {
                                  const g = await acceptChallenge(c.id);
                                  notifyPlayingChallengesChanged();
                                  navigate(`/playing/game/${g.id}`);
                                } catch {
                                  setError(t('playing.challengeAcceptFailed'));
                                } finally {
                                  setBusyId(null);
                                }
                              }}
                            >
                              {t('playing.accept')}
                            </Button>
                          )}
                          <Button
                            variant="page-secondary"
                            disabled={busyId === c.id}
                            onClick={async () => {
                              setBusyId(c.id);
                              try {
                                await declineChallenge(c.id);
                                setChallenges((prev) => prev.filter((x) => Number(x.id) !== Number(c.id)));
                                notifyPlayingChallengesChanged();
                              } catch {
                                setError(t('playing.challengeDeclineFailed', { defaultValue: 'Could not decline. Try again.' }));
                              } finally {
                                setBusyId(null);
                              }
                            }}
                          >
                            {t('playing.decline')}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {liveOutgoing.length > 0 && (
                <ul className={`divide-y divide-slate-100${liveIncoming.length > 0 ? ' border-t border-stone-100' : ''}`}>
                  {liveOutgoing.map((c) => {
                    const tc = formatTimeControl(c.timeControlSeconds, c.incrementSeconds || 0, c.daysPerMove);
                    const expires = formatExpiryCountdown(c.createdAt, challengeTtlMs(c), ttlNow);
                    return (
                      <li key={`out-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-stone-400">
                            {t('playing.outgoingChallenge', { defaultValue: 'Outgoing' })}
                          </p>
                          <p className="text-sm font-semibold text-slate-900">
                            <UserLink
                              id={c.toUserId}
                              name={c.toName || t('playing.challengePlayer')}
                              playing
                              className="text-slate-900 hover:text-primary-700"
                            />
                          </p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {tc && <span className="playing-tc-chip playing-tc-chip--sm">{tc}</span>}
                            <span className="playing-meta-pill">
                              {c.rated ? t('playing.ratedGame') : t('playing.casualGame')}
                            </span>
                            <span className="playing-meta-pill">
                              {colorPreferenceLabel(c.colorPreference, t)}
                            </span>
                            {c.spectatorAllowed === false && (
                              <span className="playing-meta-pill">{t('playing.spectatorsOff')}</span>
                            )}
                            {expires && (
                              <span className="playing-meta-pill playing-meta-pill--warn">
                                {t('playing.expiresIn', {
                                  when: expires,
                                  defaultValue: 'Expires in {{when}}',
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {c.gameId && (
                            <Button
                              variant="page-primary"
                              onClick={() => navigate(`/playing/game/${c.gameId}`)}
                            >
                              {t('playing.open')}
                            </Button>
                          )}
                          <Button
                            variant="page-secondary"
                            disabled={busyId === `out-${c.id}`}
                            onClick={async () => {
                              setBusyId(`out-${c.id}`);
                              try {
                                await cancelChallenge(c.id);
                                setOutgoing((prev) => prev.filter((x) => Number(x.id) !== Number(c.id)));
                              } catch {
                                setError(t('playing.cancelChallengeFailed'));
                              } finally {
                                setBusyId(null);
                              }
                            }}
                          >
                            {t('playing.cancelChallenge')}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
        </div>

        <aside className="playing-hub-rail">
          {loading ? (
            <div className="playing-rating-hero">
              <PlayingSkeleton rows={2} className="w-full" />
            </div>
          ) : (
            <div className="playing-rating-hero">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t('playing.yourRating')}
                </p>
                <div className="playing-hub-speed" role="tablist">
                  {HUB_SPEEDS.map((sc) => (
                    <button
                      key={sc}
                      type="button"
                      role="tab"
                      aria-selected={hubSpeed === sc}
                      className={`playing-hub-speed__btn${hubSpeed === sc ? ' playing-hub-speed__btn--active' : ''}`}
                      onClick={() => changeHubSpeed(sc)}
                    >
                      {t(`playing.speed.${sc}`, { defaultValue: sc })}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-4xl font-bold tabular-nums tracking-tight text-stone-900">
                  {rating?.rating ?? '—'}
                </p>
                <div className="playing-hub-wdl">
                  <span>W {rating?.wins ?? 0}</span>
                  <span>D {rating?.draws ?? 0}</span>
                  <span>L {rating?.losses ?? 0}</span>
                </div>
                <p className="mt-2 text-xs text-slate-500">{t('playing.hubRatingHint')}</p>
              </div>
              {rating && (
                <div className="playing-rating-hero-side">
                  <CompositionDonut
                    data={[
                      { name: t('playing.wins', { defaultValue: 'Wins' }), value: rating.wins ?? 0, color: CHART_COLORS.success },
                      { name: t('playing.draws', { defaultValue: 'Draws' }), value: rating.draws ?? 0, color: CHART_COLORS.slate },
                      { name: t('playing.losses', { defaultValue: 'Losses' }), value: rating.losses ?? 0, color: CHART_COLORS.danger },
                    ]}
                    chartSize={112}
                    showLegend={false}
                    centerValue={rating.rating ?? '—'}
                    centerLabel={t('charts.gameRecord', { defaultValue: 'Game record' })}
                  />
                </div>
              )}
            </div>
          )}

          <div className="playing-hub-shortcuts">
            {QUICK_LINKS.filter((item) => {
              if (isParentRole && item.to === '/playing/challenge') return false;
              if (item.key === 'aiCoach' && !coachLive) return false;
              return true;
            }).map((item) => {
              const blocked = matchmakingBlocked && item.to === '/playing/challenge';
              const label = t(`playing.${item.key}`, {
                defaultValue: item.key === 'coachBoard'
                  ? 'Coach board'
                  : item.key === 'aiCoach'
                    ? 'Ask AI Coach'
                    : item.key,
              });
              const challengePending = item.to === '/playing/challenge'
                && (liveIncoming.length + liveOutgoing.length) > 0;
              const desc = blocked
                ? (banned
                  ? t('playing.banQuickLinkBlocked')
                  : t('playing.matchmakingPausedShort', {
                    defaultValue: 'Matchmaking paused — retry ban check',
                  }))
                : challengePending
                  ? t('playing.challengesShortcutPending', {
                    count: liveIncoming.length + liveOutgoing.length,
                    defaultValue: '{{count}} pending — open to manage',
                  })
                  : t(`playing.${item.desc}`, {
                    defaultValue: item.desc === 'coachBoardShortcut'
                      ? 'Teaching board + Classroom'
                      : item.desc === 'aiCoachShortcut'
                        ? 'Ask about chess or your games'
                        : item.desc,
                  });
              if (blocked) {
                return (
                  <div
                    key={item.to}
                    className="playing-hub-shortcut playing-hub-shortcut--disabled"
                    title={banned
                      ? t('playing.banQuickLinkBlocked')
                      : t('playing.banStatusUnknown', {
                        defaultValue: 'Could not verify play-ban status. Matchmaking is paused until this refreshes.',
                      })}
                    aria-disabled="true"
                  >
                    <span className="playing-hub-shortcut__icon"><ShortcutMark to={item.to} /></span>
                    <p className="playing-hub-shortcut__label">{label}</p>
                    <p className="playing-hub-shortcut__desc">{desc}</p>
                  </div>
                );
              }
              return (
                <Link key={item.to} to={item.to} className="playing-hub-shortcut">
                  <span className="playing-hub-shortcut__icon">
                    <ShortcutMark to={item.to} />
                    {challengePending && (
                      <span className="playing-hub-shortcut__badge" aria-hidden>
                        {liveIncoming.length + liveOutgoing.length}
                      </span>
                    )}
                  </span>
                  <p className="playing-hub-shortcut__label">{label}</p>
                  <p className="playing-hub-shortcut__desc">{desc}</p>
                </Link>
              );
            })}
          </div>

          {(reviewReadyPeek.length > 0 || livePeek.length > 0 || livePeekError) && (
            <section className="playing-hub-panel">
              {reviewReadyPeek.length > 0 && (
                <>
                  <div className="playing-hub-panel__head">
                    <h2 className="playing-hub-panel__title">
                      {t('playing.reviewReadyStrip', { defaultValue: 'Review ready' })}
                    </h2>
                    <Button variant="page-secondary" onClick={() => navigate('/playing/games')}>
                      {t('playing.myGames')}
                    </Button>
                  </div>
                  <ul className="playing-hub-peek-list">
                    {reviewReadyPeek.map((g) => (
                      <li key={g.id} className="playing-hub-peek-row">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-900">
                            {g.whiteName || '…'}
                            {' vs '}
                            {g.blackName || '…'}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {g.myAccuracyPct != null
                              ? t('playing.accuracyChip', {
                                defaultValue: '{{pct}}% accuracy',
                                pct: g.myAccuracyPct,
                              })
                              : t('playing.reviewReady', { defaultValue: 'Review ready' })}
                          </p>
                        </div>
                        <Button
                          variant="page-primary"
                          onClick={() => navigate(`/playing/game/${g.id}?analyze=1`)}
                        >
                          {t('playing.analyze', { defaultValue: 'Analyze' })}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {livePeekError && livePeek.length === 0 && (
                <div className="px-3 py-2.5 text-xs text-amber-900" role="alert">
                  {livePeekError}
                  <Button variant="page-secondary" className="ml-2" onClick={() => load()}>
                    {t('common.retry', { defaultValue: 'Retry' })}
                  </Button>
                </div>
              )}
              {livePeek.length > 0 && (
                <>
                  <div className="playing-hub-panel__head">
                    <h2 className="playing-hub-panel__title">
                      {t('playing.liveNowPeek', { defaultValue: 'Live now' })}
                    </h2>
                    <Button variant="page-secondary" onClick={() => navigate('/playing/live')}>
                      {t('playing.viewLive')}
                    </Button>
                  </div>
                  <ul className="playing-hub-peek-list">
                    {livePeek.map((g) => (
                      <li key={g.id} className="playing-hub-peek-row">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900">
                            <UserLink
                              id={g.whiteUserId}
                              name={g.whiteName}
                              playing
                              className="text-slate-900 hover:text-primary-700"
                            />
                            {' vs '}
                            <UserLink
                              id={g.blackUserId}
                              name={g.blackName}
                              playing
                              className="text-slate-900 hover:text-primary-700"
                            />
                          </p>
                          {formatTimeControl(
                            g.timeControlSeconds,
                            g.incrementMs != null ? Math.round(g.incrementMs / 1000) : 0,
                            g.daysPerMove,
                          ) && (
                            <span className="playing-meta-pill mt-1 inline-block">
                              {formatTimeControl(
                                g.timeControlSeconds,
                                g.incrementMs != null ? Math.round(g.incrementMs / 1000) : 0,
                                g.daysPerMove,
                              )}
                            </span>
                          )}
                        </div>
                        <Button variant="page-secondary" onClick={() => navigate(`/playing/spectate/${g.id}`)}>
                          {t('playing.spectate')}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}

          {prefsLoadFailed && !prefs && (
            <PlayingBanner tone="warning">
              {t('playing.boardSettingsLoadFailed', {
                defaultValue: 'Board settings could not be loaded.',
              })}
              {' '}
              <button
                type="button"
                className="font-semibold underline"
                disabled={prefsBusy}
                onClick={async () => {
                  setPrefsBusy(true);
                  try {
                    const p = await getPlayingPrefs();
                    setPrefs(p);
                    setPrefsLoadFailed(false);
                    if (typeof p?.soundEnabled === 'boolean') setPlayingMuted(!p.soundEnabled);
                  } catch {
                    setPrefsLoadFailed(true);
                  } finally {
                    setPrefsBusy(false);
                  }
                }}
              >
                {t('playing.retry', { defaultValue: 'Retry' })}
              </button>
            </PlayingBanner>
          )}

          {prefs && (
            <PlayingDisclosure
              title={(
                <>
                  <span className="playing-hub-panel__title-icon"><MarkSettings /></span>
                  {t('playing.boardSettings', { defaultValue: 'Board settings' })}
                </>
              )}
              hint={t('playing.boardSettingsHint', {
                defaultValue: 'Theme, pieces, sound — tap to edit',
              })}
              open={boardSettingsOpen}
              onOpenChange={setBoardSettingsOpen}
            >
              {prefsNotice && (
                <p className="mb-2 text-xs font-medium text-emerald-700">{prefsNotice}</p>
              )}
              <div className="playing-hub-prefs-selects">
                <label className="text-xs font-medium text-slate-600">
                  {t('playing.boardTheme', { defaultValue: 'Board theme' })}
                  <select
                    className="select mt-1 w-full"
                    value={prefs.boardTheme || 'classic'}
                    disabled={prefsBusy}
                    onChange={(e) => savePref({ boardTheme: e.target.value })}
                  >
                    <option value="classic">{t('playing.boardThemes.classic', { defaultValue: 'Classic' })}</option>
                    <option value="green">{t('playing.boardThemes.green', { defaultValue: 'Green' })}</option>
                    <option value="brown">{t('playing.boardThemes.brown', { defaultValue: 'Brown' })}</option>
                    <option value="blue">{t('playing.boardThemes.blue', { defaultValue: 'Blue' })}</option>
                  </select>
                </label>
                <label className="text-xs font-medium text-slate-600">
                  {t('playing.pieceSet', { defaultValue: 'Piece set' })}
                  <select
                    className="select mt-1 w-full"
                    value={prefs.pieceSet || 'cburnett'}
                    disabled={prefsBusy}
                    onChange={(e) => savePref({ pieceSet: e.target.value })}
                  >
                    <option value="cburnett">{t('playing.pieceSetStandard', { defaultValue: 'Standard' })}</option>
                    <option value="unicode">{t('playing.pieceSetUnicode', { defaultValue: 'Unicode' })}</option>
                  </select>
                </label>
                <label className="text-xs font-medium text-slate-600">
                  {t('playing.animationSpeed', { defaultValue: 'Animation' })}
                  <select
                    className="select mt-1 w-full"
                    value={prefs.animationSpeed || 'normal'}
                    disabled={prefsBusy}
                    onChange={(e) => savePref({ animationSpeed: e.target.value })}
                  >
                    <option value="slow">{t('playing.animationSlow', { defaultValue: 'Slow' })}</option>
                    <option value="normal">{t('playing.animationNormal', { defaultValue: 'Normal' })}</option>
                    <option value="fast">{t('playing.animationFast', { defaultValue: 'Fast' })}</option>
                  </select>
                </label>
              </div>
              <div className="playing-hub-prefs-toggles mt-3">
                {[
                  ['soundEnabled', t('playing.sound', { defaultValue: 'Sound' })],
                  ['showCoords', t('playing.showCoordinates')],
                  ['moveConfirmation', t('playing.moveConfirmation', { defaultValue: 'Confirm moves' })],
                  ['highContrast', t('playing.highContrast', { defaultValue: 'High contrast' })],
                  ['autoQueen', t('playing.autoQueen', { defaultValue: 'Always promote to queen' })],
                  ['showBookHints', t('playing.showBookHints', { defaultValue: 'Opening book hints' })],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    disabled={prefsBusy}
                    className={`playing-hub-pref-toggle${prefs[key] ? ' playing-hub-pref-toggle--on' : ''}`}
                    onClick={() => savePref({ [key]: !prefs[key] })}
                  >
                    <span>{label}</span>
                    <span className="tabular-nums text-[10px] uppercase tracking-wide opacity-70">
                      {prefs[key] ? t('common.on', { defaultValue: 'On' }) : t('common.off', { defaultValue: 'Off' })}
                    </span>
                  </button>
                ))}
              </div>
            </PlayingDisclosure>
          )}
        </aside>
      </div>
      </div>
      {confirmDialog}
    </div>
  );
}
